import {
  Box,
  Vec,
  type BoxLike,
  type MatModel,
  type VecLike,
} from "../geometry";

/** 2D affine matrix [a c e; b d f; 0 0 1]. */
export class Mat implements MatModel {
  constructor(
    public a = 1,
    public b = 0,
    public c = 0,
    public d = 1,
    public e = 0,
    public f = 0,
  ) {}

  static Identity() {
    return new Mat();
  }
  /** Translate by (x, y) after rotating by `rotation` radians. */
  static Compose(x: number, y: number, rotation = 0) {
    const cos = Math.cos(rotation),
      sin = Math.sin(rotation);
    return new Mat(cos, sin, -sin, cos, x, y);
  }
  static Multiply(m: Mat, n: Mat) {
    return new Mat(
      m.a * n.a + m.c * n.b,
      m.b * n.a + m.d * n.b,
      m.a * n.c + m.c * n.d,
      m.b * n.c + m.d * n.d,
      m.a * n.e + m.c * n.f + m.e,
      m.b * n.e + m.d * n.f + m.f,
    );
  }
  multiply(n: Mat) {
    return Mat.Multiply(this, n);
  }
  invert() {
    const det = this.a * this.d - this.b * this.c;
    if (!det) return new Mat();
    return new Mat(
      this.d / det,
      -this.b / det,
      -this.c / det,
      this.a / det,
      (this.c * this.f - this.d * this.e) / det,
      (this.b * this.e - this.a * this.f) / det,
    );
  }
  applyToPoint(p: VecLike) {
    return new Vec(
      this.a * p.x + this.c * p.y + this.e,
      this.b * p.x + this.d * p.y + this.f,
      p.z ?? 1,
    );
  }
  applyToPoints(points: VecLike[]) {
    return points.map((p) => this.applyToPoint(p));
  }
  /** Bounds of a local box after transforming its corners. */
  applyToBounds(box: BoxLike) {
    return Box.FromPoints(
      this.applyToPoints([
        { x: box.x, y: box.y },
        { x: box.x + box.w, y: box.y },
        { x: box.x + box.w, y: box.y + box.h },
        { x: box.x, y: box.y + box.h },
      ]),
    );
  }
  rotation() {
    return Math.atan2(this.b, this.a);
  }
  point() {
    return new Vec(this.e, this.f);
  }
  toCss() {
    return `matrix(${this.a}, ${this.b}, ${this.c}, ${this.d}, ${this.e}, ${this.f})`;
  }
  clone() {
    return new Mat(this.a, this.b, this.c, this.d, this.e, this.f);
  }
}

export const rotatePoint = (p: VecLike, center: VecLike, angle: number) => {
  const cos = Math.cos(angle),
    sin = Math.sin(angle),
    dx = p.x - center.x,
    dy = p.y - center.y;
  return new Vec(
    center.x + dx * cos - dy * sin,
    center.y + dx * sin + dy * cos,
  );
};

/** Distance from p to segment ab. */
export function distanceToSegment(p: VecLike, a: VecLike, b: VecLike) {
  return Vec.Dist(p, nearestOnSegment(p, a, b));
}
export function nearestOnSegment(p: VecLike, a: VecLike, b: VecLike) {
  const dx = b.x - a.x,
    dy = b.y - a.y,
    len = dx * dx + dy * dy;
  if (!len) return new Vec(a.x, a.y);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len),
  );
  return new Vec(a.x + dx * t, a.y + dy * t);
}
/** Even-odd point-in-polygon test. */
export function pointInPolygon(p: VecLike, polygon: VecLike[]) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i],
      b = polygon[j];
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}
export function segmentsIntersect(
  a: VecLike,
  b: VecLike,
  c: VecLike,
  d: VecLike,
) {
  const cross = (p: VecLike, q: VecLike, r: VecLike) =>
    (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = cross(c, d, a),
    d2 = cross(c, d, b),
    d3 = cross(a, b, c),
    d4 = cross(a, b, d);
  return (
    d1 * d2 <= 0 &&
    d3 * d4 <= 0 &&
    !(d1 === 0 && d2 === 0 && d3 === 0 && d4 === 0)
  );
}
